import { useState, useEffect } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getProjects, handleNumericInput } from "../lib/api";
import { FolderKanban, Plus, Trash2, Percent } from "lucide-react";

function AllocationRow({ alloc, index, projects, totalAmount, onChange, onRemove, showRemove }) {
  const [lineItems, setLineItems] = useState([]);
  const [mode, setMode] = useState("amount");

  useEffect(() => {
    if (alloc.project_id) {
      const proj = projects.find(p => p.id === alloc.project_id);
      setLineItems(proj?.line_items || []);
    } else {
      setLineItems([]);
    }
  }, [alloc.project_id, projects]);

  const handlePercentChange = (val) => {
    const pct = parseFloat(val) || 0;
    const amt = totalAmount > 0 ? Math.round((pct / 100) * totalAmount * 100) / 100 : 0;
    onChange({ ...alloc, percent: val, amount: String(amt) });
  };

  const handleAmountChange = (rawVal) => {
    const val = handleNumericInput(rawVal);
    const amt = parseFloat(val) || 0;
    const pct = totalAmount > 0 ? Math.round((amt / totalAmount) * 10000) / 100 : 0;
    onChange({ ...alloc, amount: val, percent: String(pct) });
  };

  return (
    <div className="space-y-2.5 p-3 bg-white rounded-md border border-slate-200" data-testid={`alloc-row-${index}`}>
      {/* Amount — ALWAYS visible */}
      <div>
        <Label className="text-xs text-slate-500 font-medium">Amount to allocate</Label>
        <div className="flex items-center gap-2 mt-1">
          <div className="relative flex-1">
            {mode === "percent" ? (
              <Input
                type="text"
                inputMode="decimal"
                value={alloc.percent || ""}
                onChange={(e) => handlePercentChange(e.target.value)}
                placeholder="e.g. 60"
                className="h-9 text-sm"
                data-testid={`alloc-percent-${index}`}
              />
            ) : (
              <>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rs</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={alloc.amount || ""}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder={totalAmount > 0 ? String(totalAmount) : "Amount"}
                  className="h-9 text-sm pl-9"
                  data-testid={`alloc-amount-${index}`}
                />
              </>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 px-3 text-xs shrink-0"
            onClick={() => {
              if (mode === "amount") {
                const amt = parseFloat(alloc.amount) || 0;
                const pct = totalAmount > 0 ? Math.round((amt / totalAmount) * 10000) / 100 : 0;
                onChange({ ...alloc, percent: String(pct) });
                setMode("percent");
              } else {
                setMode("amount");
              }
            }}
            data-testid={`toggle-mode-${index}`}
          >
            <Percent className="w-3.5 h-3.5 mr-1" />
            {mode === "amount" ? "Use %" : "Use Rs"}
          </Button>
          {showRemove && (
            <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-red-400 hover:text-red-600 shrink-0" onClick={onRemove} data-testid={`remove-alloc-${index}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        {alloc.amount && totalAmount > 0 && (
          <p className="text-xs text-slate-400 mt-1">
            {parseFloat(alloc.percent || 0).toFixed(1)}% of Rs {parseFloat(totalAmount).toLocaleString("en-IN")} = Rs {parseFloat(alloc.amount || 0).toLocaleString("en-IN")}
          </p>
        )}
      </div>

      {/* Project & Line Item */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-500">Project</Label>
          <Select
            value={alloc.project_id || "none"}
            onValueChange={(v) => onChange({ ...alloc, project_id: v === "none" ? "" : v, line_item_id: "" })}
          >
            <SelectTrigger className="h-9 text-sm" data-testid={`select-project-${index}`}><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Line Item</Label>
          <Select
            value={alloc.line_item_id || "none"}
            onValueChange={(v) => onChange({ ...alloc, line_item_id: v === "none" ? "" : v })}
            disabled={!alloc.project_id}
          >
            <SelectTrigger className="h-9 text-sm" data-testid={`select-line-item-${index}`}><SelectValue placeholder={alloc.project_id ? "Select" : "Select project first"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {lineItems.map(li => <SelectItem key={li.id} value={li.id}>{li.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export function ProjectAllocation({ allocations, totalAmount, onChange }) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getProjects().then(res => setProjects(res.data)).catch(() => {});
  }, []);

  const handleRowChange = (index, updated) => {
    const next = [...allocations];
    next[index] = updated;
    onChange(next);
  };

  const handleAddRow = () => {
    onChange([...allocations, { project_id: "", line_item_id: "", amount: "", percent: "" }]);
  };

  const handleRemoveRow = (index) => {
    const next = allocations.filter((_, i) => i !== index);
    if (next.length === 0) next.push({ project_id: "", line_item_id: "", amount: "", percent: "" });
    onChange(next);
  };

  const totalAllocated = allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const totalPct = totalAmount > 0 ? Math.round((totalAllocated / totalAmount) * 10000) / 100 : 0;
  const hasAnyAllocation = allocations.some(a => parseFloat(a.amount) > 0);

  return (
    <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2.5 bg-slate-50/50" data-testid="project-allocation-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <FolderKanban className="w-4 h-4" />
          Allocate to Project (Optional)
        </div>
        {allocations.length < 3 && (
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-brand-orange hover:text-brand-orange gap-1" onClick={handleAddRow} data-testid="add-allocation-btn">
            <Plus className="w-3 h-3" /> Add
          </Button>
        )}
      </div>

      <p className="text-xs text-slate-400">Full amount is deducted from bank. Optionally tag part or full amount to a project for tracking.</p>

      {allocations.map((alloc, i) => (
        <AllocationRow
          key={i}
          alloc={alloc}
          index={i}
          projects={projects}
          totalAmount={totalAmount}
          onChange={(updated) => handleRowChange(i, updated)}
          onRemove={() => handleRemoveRow(i)}
          showRemove={allocations.length > 1}
        />
      ))}

      {hasAnyAllocation && (
        <div className="flex justify-between text-xs pt-1.5 border-t border-slate-200">
          <span className="text-slate-500">Total allocated to projects</span>
          <span className="font-semibold text-brand-navy">
            Rs {totalAllocated.toLocaleString("en-IN")} ({totalPct}%)
          </span>
        </div>
      )}
    </div>
  );
}
